// src/lib/matchThreshold.js
//
// Ticket 27 -- "The honest empty state." One named value, one home, read by
// its three consumers exactly as the ticket asks:
//
//   1. The craving deck (MatchMeCard.jsx) -- labels each result "X% Match"
//      at or above this, "Closest: X%" below it. Both cases still SHOW the
//      book: this constant never removes a result from the deck, it only
//      decides what the badge says. match_books is deliberately called
//      WITHOUT its own similarity_threshold argument for this reason --
//      that argument (added in ticket 22) is a hard filter, and a hard
//      filter is the opposite of what "show the closest, labelled as
//      closest" asks for.
//   2. Pipeline suppression (ReaderHomepage.jsx) -- the Popular -> Hidden
//      Gem strip shows only when at least one returned pipeline's
//      similarity clears this value; when none do, the strip does not
//      render at all, per the ticket ("show no pipelines," not a strip of
//      weak ones).
//   3. The empty-state screen itself -- a craving's best result (or its
//      lack of one) is compared against this same value to decide whether
//      the "closest, not matched" framing and the journal offer apply.
//
// THE NUMBER ITSELF IS A PLACEHOLDER, NOT A MEASURED VALUE.
//
// There is no real craving/match data to calibrate against yet -- this
// migration and the craving deck it feeds are what starts collecting it
// (cravings.best_match_similarity, added in the same pull request as this
// file). 0.6 is a starting guess based on how OpenAI's embedding models
// generally behave, not this catalogue specifically: cosine similarity
// between genuinely related book descriptions commonly falls in the
// 0.55-0.85 range, while even unrelated text on this kind of model rarely
// drops much below roughly 0.3-0.4 -- there is no natural floor at 0, so a
// threshold has to sit well above 0.5 to mean anything. Once real cravings
// have run and ticket 37's admin view can show the actual distribution of
// best_match_similarity across them, THIS is the number to revisit -- not a
// guess to leave in place indefinitely.
//
// Expressed as a similarity in [0, 1] (1 = identical), the same vocabulary
// match_books' own similarity_threshold argument and match_pipelines' new
// similarity column already use -- never a raw pgvector distance, and never
// re-derived independently at any of the three call sites above.
export const SIMILARITY_THRESHOLD = 0.6;

// Converts match_books' or match_pipelines' returned `distance` into the
// same [0, 1] similarity SIMILARITY_THRESHOLD is expressed in. NULL in,
// NULL out -- a NULL distance means "cannot say how close" (no embedding on
// one side or the other), never "0% match," and every caller of this
// function has to keep that distinction rather than defaulting a NULL to 0.
export function similarityFromDistance(distance) {
  if (distance === null || distance === undefined) return null;
  return 1 - distance;
}

// True when a similarity clears the bar -- the one comparison every
// consumer above should call through, rather than writing
// `similarity >= SIMILARITY_THRESHOLD` in three different files where a
// future change to the operator (say, to `>`) would have to be caught and
// fixed in all three by hand.
export function clearsThreshold(similarity) {
  return similarity !== null && similarity !== undefined && similarity >= SIMILARITY_THRESHOLD;
}
